import { motion, useReducedMotion, type HTMLMotionProps } from 'framer-motion';
import { publicMotion, publicTransition } from '../lib/motion';

interface ScrollRevealProps extends Omit<HTMLMotionProps<'div'>, 'initial' | 'whileInView' | 'viewport' | 'transition'> {
  delay?: number;
  as?: 'div' | 'section';
}

/** Fades/translates a section into view once, on scroll. Respects reduced motion. */
export function ScrollReveal({ delay = 0, as = 'div', ...props }: ScrollRevealProps) {
  const reducedMotion = useReducedMotion();
  const Component = motion[as];
  return (
    <Component
      initial={reducedMotion ? false : { opacity: 0, y: publicMotion.distance.interface }}
      whileInView={{ opacity: 1, y: 0 }}
      /*
       * `amount` is deliberately NOT a fraction of the element.
       *
       * This used to be `amount: 0.3`, which asks for 30% of the element to be
       * on screen. That is unreachable for any section taller than ~3.3x the
       * viewport: the largest fraction ever visible is viewportHeight/elementH,
       * so a 1459px stack in an 844px window peaks at 58%, but the same stack
       * in a short window (landscape phone, a small laptop, a resized browser)
       * peaks below 30% and the section then NEVER reveals — it stays at
       * opacity 0 permanently, with the content present in the DOM and
       * invisible to the reader. Measured at 16-22% on /customers, /faq and
       * /integrations.
       *
       * A margin-based trigger has no such ceiling: it fires when the element's
       * top edge crosses 88% of the viewport height, which is reachable for an
       * element of any size, while still holding the reveal until the section
       * is genuinely arriving rather than firing at the very bottom edge.
       */
      viewport={{ once: true, margin: '0px 0px -12% 0px' }}
      transition={{ ...publicTransition(!!reducedMotion, publicMotion.duration.scene), delay: reducedMotion ? 0 : delay }}
      {...props}
    />
  );
}

/** Per-item stagger delay for cards revealed together as a group (use with ScrollReveal in a map()). */
export function staggerDelay(index: number, step = 0.06) {
  return index * step;
}
